import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { postTicketFlowAction } from "../../api/ticketFlow";

const TEAL = "#00a884";
const HEADER_BG = "#075e54";

function isPlaceholder(id) {
  const s = String(id || "").trim();
  return !s || s === "_";
}

function realOptions(list) {
  return (Array.isArray(list) ? list : []).filter((row) => row?.id && !isPlaceholder(row.id));
}

function screenTitle(screen, t) {
  const key = `ticketFlow.screens.${String(screen || "ORIGIN")}`;
  const label = t(key);
  return label === key ? t("ticketFlow.title") : label;
}

function mapImageSrc(raw, showMap) {
  if (!showMap) return "";
  const value = String(raw || "").trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value) || value.startsWith("data:")) return value;
  return `data:image/png;base64,${value}`;
}

function OptionList({ name, options, value, onChange, disabled }) {
  const rows = realOptions(options);
  if (!rows.length) return null;
  return (
    <RadioGroup
      name={name}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
    >
      {rows.map((row) => (
        <FormControlLabel
          key={row.id}
          value={row.id}
          disabled={disabled || row.enabled === false}
          control={<Radio size="small" sx={{ color: TEAL, "&.Mui-checked": { color: TEAL } }} />}
          label={
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>{row.title}</Typography>
              {row.description ? (
                <Typography sx={{ color: "text.secondary", fontSize: "0.8rem" }}>
                  {row.description}
                </Typography>
              ) : null}
            </Box>
          }
          sx={{
            alignItems: "flex-start",
            mx: 0,
            py: 0.75,
            borderBottom: "1px solid rgba(0,0,0,0.06)",
            width: "100%",
          }}
        />
      ))}
    </RadioGroup>
  );
}

export default function TicketFlowWizard({ token, onClose, embedded = false }) {
  const { t } = useTranslation("chat");
  const [screen, setScreen] = useState("ORIGIN");
  const [data, setData] = useState({});
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [fatal, setFatal] = useState("");

  const exchange = useCallback(
    async ({ action, nextScreen, nextData, replaceForm }) => {
      if (!token) return;
      setBusy(true);
      try {
        const result = await postTicketFlowAction({
          token,
          action,
          screen: nextScreen != null ? nextScreen : screen,
          data: nextData != null ? nextData : form,
        });
        const next = result?.screen || "ORIGIN";
        const payload = result?.data && typeof result.data === "object" ? result.data : {};
        setScreen(next);
        setData(payload);
        setForm(replaceForm || {});
        setFatal("");
      } catch (err) {
        const status = err?.response?.status;
        const message =
          err?.response?.data?.message ||
          err?.response?.data?.data?.error_message ||
          t("ticketFlow.error");
        if (status === 410 || status === 400) {
          setFatal(message);
        } else {
          setData((prev) => ({ ...prev, error_message: message }));
        }
      } finally {
        setLoading(false);
        setBusy(false);
      }
    },
    [form, screen, t, token]
  );

  useEffect(() => {
    if (!token) {
      setFatal(t("ticketFlow.missingToken"));
      setLoading(false);
      return;
    }
    exchange({ action: "INIT", nextScreen: "ORIGIN", nextData: {}, replaceForm: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const errorMessage = String(data.error_message || "").trim();
  const done = data.done === true || screen === "SUMMARY";
  const canGoBack = screen !== "ORIGIN" && !done;

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const submit = (extra = {}) => {
    if (busy) return;
    exchange({ action: "DATA_EXCHANGE", nextData: { ...form, ...extra } });
  };

  const pickOption = (name, id, extra = {}) => {
    if (busy || isPlaceholder(id)) return;
    const next = { ...form, [name]: id, ...extra };
    setForm(next);
    exchange({ action: "DATA_EXCHANGE", nextData: next, replaceForm: {} });
  };

  const header = (
    <Box
      sx={{
        bgcolor: HEADER_BG,
        color: "#fff",
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        px: 1,
        py: 1.1,
        minHeight: 56,
      }}
    >
      {canGoBack ? (
        <IconButton
          aria-label={t("ticketFlow.back")}
          onClick={() => exchange({ action: "BACK", nextData: {}, replaceForm: {} })}
          disabled={busy}
          sx={{ color: "#fff" }}
        >
          <ArrowBackIcon />
        </IconButton>
      ) : (
        <Box sx={{ width: 40 }} />
      )}
      <Typography sx={{ flex: 1, fontWeight: 700, textAlign: "center" }}>
        {screenTitle(screen, t)}
      </Typography>
      {onClose ? (
        <IconButton aria-label={t("webchat.close")} onClick={onClose} sx={{ color: "#fff" }}>
          <CloseIcon />
        </IconButton>
      ) : (
        <Box sx={{ width: 40 }} />
      )}
    </Box>
  );

  const footer = (label, onClick) => (
    <Button
      fullWidth
      variant="contained"
      disabled={busy}
      onClick={onClick}
      sx={{
        mt: 2,
        py: 1.2,
        bgcolor: TEAL,
        textTransform: "none",
        fontWeight: 700,
        fontSize: "1rem",
        "&:hover": { bgcolor: "#00a884" },
      }}
    >
      {busy ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : label}
    </Button>
  );

  const body = useMemo(() => {
    if (loading) {
      return (
        <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
          <CircularProgress sx={{ color: TEAL }} />
        </Box>
      );
    }
    if (fatal) {
      return (
        <Alert severity="error" sx={{ mt: 2 }}>
          {fatal}
        </Alert>
      );
    }

    const blocks = [];
    if (errorMessage) {
      blocks.push(
        <Alert key="err" severity="warning" sx={{ mb: 1.5 }}>
          {errorMessage}
        </Alert>
      );
    }
    if (data.origin_line) {
      blocks.push(
        <Typography key="origin-line" sx={{ mb: 1, color: "text.secondary" }}>
          {data.origin_line}
        </Typography>
      );
    }
    if (data.route_label) {
      blocks.push(
        <Typography key="route" sx={{ mb: 1, fontWeight: 600 }}>
          {data.route_label}
        </Typography>
      );
    }
    if (data.seat_label) {
      blocks.push(
        <Typography key="seat-label" sx={{ mb: 1, fontWeight: 600 }}>
          {data.seat_label}
        </Typography>
      );
    }
    if (data.passenger_label) {
      blocks.push(
        <Typography key="pax-label" sx={{ mb: 1, fontWeight: 600 }}>
          {data.passenger_label}
        </Typography>
      );
    }
    if (data.gratuity_label) {
      blocks.push(
        <Typography key="grat-label" sx={{ mb: 1, fontWeight: 600 }}>
          {data.gratuity_label}
        </Typography>
      );
    }

    const mapSrc = mapImageSrc(data.seat_map, data.show_map);
    if (mapSrc) {
      blocks.push(
        <Box
          key="map"
          component="img"
          src={mapSrc}
          alt=""
          sx={{ width: "100%", maxHeight: 220, objectFit: "contain", mb: 1, borderRadius: 1 }}
        />
      );
    }
    if (data.seat_caption) {
      blocks.push(
        <Typography key="seat-cap" sx={{ mb: 1, color: "text.secondary", fontSize: "0.85rem" }}>
          {data.seat_caption}
        </Typography>
      );
    }

    if (screen === "ORIGIN" || screen === "DESTINATION") {
      const queryName = screen === "ORIGIN" ? "origin_query" : "destination_query";
      const choiceName = screen === "ORIGIN" ? "origin_id" : "destination_id";
      const queryLabel =
        screen === "ORIGIN" ? t("ticketFlow.originQuery") : t("ticketFlow.destinationQuery");
      if (data.show_search !== false) {
        blocks.push(
          <TextField
            key="query"
            fullWidth
            label={queryLabel}
            value={form[queryName] || ""}
            onChange={(e) => setField(queryName, e.target.value)}
            helperText={t("ticketFlow.searchHint")}
            sx={{ mb: 1 }}
          />
        );
      }
      if (data.show_choices) {
        blocks.push(
          <OptionList
            key="cities"
            name={choiceName}
            options={data.cities}
            value={form[choiceName]}
            disabled={busy}
            onChange={(id) => pickOption(choiceName, id)}
          />
        );
      }
      blocks.push(footer(t("ticketFlow.continue"), () => submit()));
    } else if (screen === "TRIP_SETUP") {
      blocks.push(
        <OptionList
          key="types"
          name="trip_type"
          options={data.trip_types}
          value={form.trip_type || data.trip_default || "one_way"}
          disabled={busy}
          onChange={(id) => {
            setField("trip_type", id);
            exchange({
              action: "DATA_EXCHANGE",
              nextData: { ...form, trip_type: id, intent: "toggle_return" },
            });
          }}
        />
      );
      blocks.push(
        <TextField
          key="ida"
          type="date"
          fullWidth
          label={t("ticketFlow.travelDate")}
          InputLabelProps={{ shrink: true }}
          value={form.travel_date || ""}
          onChange={(e) => setField("travel_date", e.target.value)}
          inputProps={{ min: data.min_date, max: data.max_date }}
          sx={{ mt: 1.5 }}
        />
      );
      if (data.show_return || form.trip_type === "round_trip") {
        blocks.push(
          <TextField
            key="volta"
            type="date"
            fullWidth
            label={t("ticketFlow.returnDate")}
            InputLabelProps={{ shrink: true }}
            value={form.return_date || ""}
            onChange={(e) => setField("return_date", e.target.value)}
            inputProps={{ min: data.min_date, max: data.max_date }}
            sx={{ mt: 1.5 }}
          />
        );
      }
      blocks.push(
        <FormControl fullWidth sx={{ mt: 1.5 }} key="qty">
          <InputLabel>{t("ticketFlow.passengerCount")}</InputLabel>
          <Select
            label={t("ticketFlow.passengerCount")}
            value={form.passenger_count || data.count_default || "1"}
            onChange={(e) => setField("passenger_count", e.target.value)}
          >
            {realOptions(data.counts).map((row) => (
              <MenuItem key={row.id} value={row.id}>
                {row.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
      blocks.push(footer(t("ticketFlow.seeTrips"), () => submit()));
    } else if (screen === "TRIPS" || screen === "TRIPS_RETURN") {
      blocks.push(
        <OptionList
          key="trips"
          name="trip_id"
          options={data.trips}
          value={form.trip_id}
          disabled={busy}
          onChange={(id) => pickOption("trip_id", id)}
        />
      );
      blocks.push(footer(t("ticketFlow.continue"), () => submit()));
    } else if (screen === "SEATS" || screen === "SEATS_RETURN") {
      blocks.push(
        <OptionList
          key="seats"
          name="seat_id"
          options={data.seats}
          value={form.seat_id}
          disabled={busy}
          onChange={(id) => pickOption("seat_id", id, { intent: id === "__more" ? "more" : "pick" })}
        />
      );
      blocks.push(footer(t("ticketFlow.continue"), () => submit({ intent: "more" })));
    } else if (String(screen).startsWith("PASSENGER_")) {
      blocks.push(
        <TextField
          key="name"
          fullWidth
          label={t("ticketFlow.passengerName")}
          value={form.passenger_name || ""}
          onChange={(e) => setField("passenger_name", e.target.value)}
          sx={{ mb: 1.5 }}
        />
      );
      blocks.push(
        <TextField
          key="cpf"
          fullWidth
          label={t("ticketFlow.passengerCpf")}
          value={form.passenger_cpf || ""}
          onChange={(e) => setField("passenger_cpf", e.target.value)}
          sx={{ mb: 1.5 }}
        />
      );
      if (data.show_birth !== false) {
        blocks.push(
          <TextField
            key="birth"
            type="date"
            fullWidth
            label={t("ticketFlow.birthDate")}
            InputLabelProps={{ shrink: true }}
            value={form.birth_date || ""}
            onChange={(e) => setField("birth_date", e.target.value)}
            inputProps={{ min: "1900-01-01", max: data.max_birth }}
            sx={{ mb: 1.5 }}
          />
        );
      }
      if (data.show_category) {
        blocks.push(
          <FormControl fullWidth sx={{ mb: 1.5 }} key="cat">
            <InputLabel>{t("ticketFlow.category")}</InputLabel>
            <Select
              label={t("ticketFlow.category")}
              value={form.category_id || data.category_default || ""}
              onChange={(e) => setField("category_id", e.target.value)}
            >
              {realOptions(data.categories).map((row) => (
                <MenuItem key={row.id} value={row.id}>
                  {row.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      }
      blocks.push(footer(t("ticketFlow.continue"), () => submit()));
    } else if (String(screen).startsWith("GRATUITY_")) {
      blocks.push(
        <TextField
          key="birth"
          fullWidth
          label={t("ticketFlow.gratuityBirth")}
          value={form.birth_date || ""}
          onChange={(e) => setField("birth_date", e.target.value)}
          sx={{ mb: 1.5 }}
        />
      );
      blocks.push(
        <TextField
          key="income"
          fullWidth
          label={t("ticketFlow.income")}
          value={form.income || ""}
          onChange={(e) => setField("income", e.target.value)}
          sx={{ mb: 1.5 }}
        />
      );
      blocks.push(
        <TextField
          key="decl"
          fullWidth
          label={t("ticketFlow.declaration")}
          value={form.declaration || ""}
          onChange={(e) => setField("declaration", e.target.value)}
          sx={{ mb: 1.5 }}
        />
      );
      blocks.push(footer(t("ticketFlow.continue"), () => submit()));
    } else if (screen === "SUMMARY") {
      blocks.push(
        <Typography key="summary" sx={{ whiteSpace: "pre-line", mb: 1 }}>
          {data.summary || t("ticketFlow.doneBody")}
        </Typography>
      );
      if (done) {
        blocks.push(
          <Alert key="done" severity="success" sx={{ mt: 1 }}>
            {t("ticketFlow.doneHint")}
          </Alert>
        );
        if (onClose) blocks.push(footer(t("ticketFlow.backToChat"), onClose));
      } else {
        blocks.push(
          footer(t("ticketFlow.send"), () =>
            exchange({ action: "COMPLETE", nextScreen: "SUMMARY", nextData: { done: "1" } })
          )
        );
      }
    }

    return blocks;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy, data, done, errorMessage, fatal, form, loading, onClose, screen, t]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: embedded ? "100%" : "100dvh",
        bgcolor: "#f0f2f5",
      }}
    >
      {header}
      <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 2 }}>{body}</Box>
    </Box>
  );
}
